/**
 * NotePreviewModal — read-only preview of a completed note.
 *
 * Opens via the Preview button on CompletedNoteCard. Fetches markdown from
 * GET /api/notes/:id/preview via the existing useNotePreview hook and renders
 * it with ReactMarkdown (same renderer used by NoteEditorPage).
 *
 * Accessibility:
 *   - role="dialog", aria-modal="true", aria-labelledby pointing to title
 *   - Focus trap: first focusable element receives focus on mount
 *   - Focus restored to the triggering element on close
 *   - Body scroll lock while open
 *   - Esc key closes the modal
 *   - Backdrop click closes the modal
 */

import { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import type { CopyState } from './useCopyToEmr';
import { useNotePreview } from './useNotePreview';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface NotePreviewModalProps {
  noteId: string;
  noteTitle: string;
  onClose: () => void;
  onCopyToEmr: () => void;
  copyState: CopyState;
}

// ---------------------------------------------------------------------------
// Modal component
// ---------------------------------------------------------------------------

export function NotePreviewModal({
  noteId,
  noteTitle,
  onClose,
  onCopyToEmr,
  copyState,
}: NotePreviewModalProps) {
  const { markdown, loading, error, refresh } = useNotePreview(noteId, true);

  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  // Capture the element that had focus before the modal opened so we can
  // restore it on close.
  const previouslyFocusedRef = useRef<Element | null>(null);

  // Body scroll lock + focus management
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    document.body.style.overflow = 'hidden';
    // Focus the close button on open
    closeBtnRef.current?.focus();

    return () => {
      document.body.style.overflow = '';
      // Restore focus to the triggering element
      (previouslyFocusedRef.current as HTMLElement | null)?.focus();
    };
  }, []);

  // Esc key listener
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }

      // Focus trap: keep focus inside the dialog
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Determine body content
  let bodyContent: React.ReactNode;
  if (loading) {
    bodyContent = (
      <div className="flex items-center justify-center py-12">
        <SpinnerIcon />
        <span className="ms-2 text-sm font-body text-clinical-text-muted">Loading preview…</span>
      </div>
    );
  } else if (error) {
    bodyContent = (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <p className="text-sm font-body text-safety-red">{error}</p>
        <button type="button" className="btn-secondary" onClick={() => refresh()}>
          Retry
        </button>
      </div>
    );
  } else if (!markdown) {
    bodyContent = (
      <p className="text-sm font-body text-clinical-text-muted text-center py-12">
        Preview not available — open in editor.
      </p>
    );
  } else {
    bodyContent = (
      <div className="prose prose-sm max-w-none font-body text-clinical-charcoal">
        <ReactMarkdown>{markdown}</ReactMarkdown>
      </div>
    );
  }

  return (
    /* Backdrop — not aria-hidden so the dialog inside is reachable by AT */
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
      data-testid="modal-backdrop"
    >
      {/* Dialog panel — stop propagation so clicks inside don't close */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="note-preview-title"
        className="
          relative bg-white w-full
          sm:max-w-[720px] sm:rounded-clinical sm:shadow-clinical-lg
          flex flex-col
          h-full sm:h-auto sm:max-h-[85vh]
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-clinical-border flex-shrink-0">
          <h2
            id="note-preview-title"
            className="font-heading text-base font-semibold text-clinical-charcoal truncate pe-4"
          >
            {noteTitle}
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            className="btn-ghost p-1.5 flex-shrink-0"
            onClick={onClose}
            aria-label="Close preview"
          >
            <XIcon />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          {bodyContent}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-clinical-border flex-shrink-0">
          <button
            type="button"
            className="btn-primary px-4 py-2 text-sm"
            onClick={onCopyToEmr}
            disabled={copyState === 'copying'}
            aria-label={`Copy ${noteTitle} to EMR`}
          >
            {copyState === 'copying'
              ? 'Copying…'
              : copyState === 'success'
              ? 'Copied'
              : copyState === 'error'
              ? 'Copy failed'
              : 'Copy to EMR'}
          </button>
          <button
            type="button"
            className="btn-secondary px-4 py-2 text-sm"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline icons
// ---------------------------------------------------------------------------

function SpinnerIcon() {
  return (
    <svg
      className="w-4 h-4 animate-spin text-clinical-teal"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
